import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo } from 'react';
import type { CSSObjectWithLabel, GroupBase, StylesConfig } from 'react-select';
import Select, { components, InputProps, OptionProps, SingleValueProps } from 'react-select';

import { useStore } from '../../hooks/use-store';
import { Sap } from '../../types/users-types';
import './PracticeSelector.scss';

interface PracticeSelectorProps {
  disabled?: boolean;
}

interface OptionType {
  value: string;
  label: string;
  sap: Sap;
}

const SingleValueComponent = components.SingleValue as React.ComponentType<
  SingleValueProps<OptionType>
>;
const OptionComponent = components.Option as React.ComponentType<OptionProps<OptionType>>;
const InputComponent = components.Input as React.ComponentType<InputProps<OptionType>>;

const getCustomStyles = (
  componentDisabled: boolean,
  isFocused: boolean,
): StylesConfig<OptionType, false, GroupBase<OptionType>> => ({
  control: (base, state) =>
    ({
      ...base,
      width: '400px',
      fontSize: '14px',
      lineHeight: '1.3em',
      boxSizing: 'border-box',
      fontFamily: 'var(--spot-font-family-sans-serif)',
      minHeight: '38px',
      height: '38px',
      backgroundColor: state.isDisabled ? 'var(--spot-color-neutral-50)' : 'inherit',
      border: 'none',
      boxShadow: 'none',
      cursor: componentDisabled ? 'default' : 'pointer',
    }) as CSSObjectWithLabel,
  valueContainer: base =>
    ({
      ...base,
      padding: 0,
      backgroundColor: isFocused ? 'white' : 'inherit',
      height: '38px',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    }) as CSSObjectWithLabel,
  singleValue: base =>
    ({
      ...base,
      margin: 0,
      color: 'var(--spot-color-neutral-800)',
      fontFamily: 'var(--spot-font-family-sans-serif)',
      lineHeight: '1.3em',
      width: '100%',
      overflow: 'visible',
    }) as CSSObjectWithLabel,
  input: base =>
    ({
      ...base,
      padding: '0 10px',
      color: 'var(--spot-color-neutral-800)',
      fontFamily: 'var(--spot-font-family-sans-serif)',
    }) as CSSObjectWithLabel,
  indicatorsContainer: base => ({ ...base, display: 'none' }) as CSSObjectWithLabel,
  menu: base =>
    ({
      ...base,
      top: '31px',
      border: '1px solid var(--spot-color-neutral-200)',
      boxShadow: '0 7px 14px 0 rgb(50 50 93 / 10%), 0 3px 6px 0 rgb(0 0 0 / 7%)',
      borderRadius: '4px',
      zIndex: 1000,
    }) as CSSObjectWithLabel,
  menuList: base =>
    ({ ...base, padding: 0, marginBottom: 0, maxHeight: '335px' }) as CSSObjectWithLabel,
  option: base =>
    ({
      ...base,
      fontFamily: 'var(--spot-font-family-sans-serif)',
      padding: '4px 10px',
      fontSize: '12px',
      lineHeight: '1.3em',
      backgroundColor: 'var(--spot-color-neutral-50)',
      cursor: 'pointer',
    }) as CSSObjectWithLabel,
});

const PracticeSelector = observer((props: PracticeSelectorProps) => {
  const { disabled } = props;
  const { globalStore, apiStore } = useStore();
  const componentDisabled = disabled || globalStore.userSaps.length <= 1;
  const [inputValue, setInputValue] = React.useState<string>('');
  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  const selectRef = React.useRef<any>(null);

  const { userSaps, selectedSap } = globalStore;

  const options: OptionType[] = useMemo(
    () => userSaps.map(sap => ({ value: sap.id, label: sap.name, sap })),
    [userSaps],
  );

  const selectedOption: OptionType | null = useMemo(() => {
    if (!selectedSap.id) return null;
    return options.find(opt => opt.value === selectedSap.id) || null;
  }, [options, selectedSap.id]);

  const filterOption = useCallback(
    (option: { data: OptionType; label: string; value: string }, newValue: string): boolean => {
      const searchValue = newValue.trim().toLowerCase();
      if (!searchValue) return true;
      // Filter by both id (value) and name (label)
      return (
        option.value?.toLowerCase().includes(searchValue) ||
        option.label?.toLowerCase().includes(searchValue)
      );
    },
    [],
  );

  const SingleValue = useCallback(
    (valueProps: SingleValueProps<OptionType>) => {
      if (inputValue || isFocused) {
        return null;
      }
      return (
        <SingleValueComponent {...valueProps}>
          <div className='practice-selector-value' data-testid='practice-selector-value'>
            <span className='spot-link--bold'>{apiStore.userName}</span>
            <span>
              {selectedSap.name} ({selectedSap.id})
            </span>
            <div
              className='down-arrow'
              style={{ visibility: componentDisabled ? 'hidden' : 'visible' }}
            />
          </div>
        </SingleValueComponent>
      );
    },
    [inputValue, isFocused, apiStore.userName, selectedSap.name, selectedSap.id, componentDisabled],
  );

  const Option = useCallback((typeOptionProps: OptionProps<OptionType>) => {
    const { data } = typeOptionProps;
    return (
      <OptionComponent {...typeOptionProps}>
        <div className='practice-selector-option' data-testid='practice-selector-option'>
          <div>{data.sap.id}</div>
          <span>{data.sap.name}</span>
        </div>
      </OptionComponent>
    );
  }, []);

  const Input = useCallback(
    (inputProps: InputProps<OptionType>) => (
      <InputComponent
        {...inputProps}
        data-testid='practice-selector-search'
        aria-label='Search practice'
      />
    ),
    [],
  );

  const styles = useMemo(
    () => getCustomStyles(componentDisabled, isFocused),
    [componentDisabled, isFocused],
  );

  if (userSaps.length === 0) return null;

  return (
    <div className='practice-selector_container'>
      <Select<OptionType>
        ref={selectRef}
        options={options}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(newValue, { action }) => {
          if (action === 'input-change') {
            setInputValue(newValue);
          }
        }}
        /*
        onChange={option => {
          if (option) {
            globalStore.changeSelectedSap(option.value, false);
            setTimeout(() => selectRef.current?.blur(), 0);
          }
        }}
        */
        onMenuOpen={() => setIsFocused(true)}
        onMenuClose={() => {
          setIsFocused(false);
          setInputValue('');
        }}
        isDisabled={componentDisabled}
        isSearchable={true}
        filterOption={filterOption}
        components={{ SingleValue, Option, Input }}
        styles={styles}
        placeholder='Enter Practice'
        classNamePrefix='practice-selector'
        menuPlacement='auto'
        blurInputOnSelect={true}
        closeMenuOnSelect={true}
        escapeClearsValue={false}
      />
    </div>
  );
});

export default PracticeSelector;
